<template>
  <div class="cure-page">
    <div class="top-bar">
      <a class="back-link" href="#" @click.prevent="$emit('back')">← Back to Benevolism</a>
      <div class="top-brand"><span class="mark">B</span> THE BENEVOLISM SERIES — VOL. II</div>
    </div>

    <main>
      <!-- HERO -->
      <section class="hero">
        <v-container class="hero-inner" style="max-width:1180px;">
          <div class="cover-col intro-rise">
            <div class="cover-card">
              <div class="cover-crown">♛</div>
              <div class="cover-title">THE<br><strong>BENEVOLENT<br>CURE</strong></div>
              <div class="cover-sub">How Benevolism Would Transform Healthcare,<br>Restore Affordability, and Heal the People<br>the System Has Abandoned</div>
              <div class="cover-divider" />
              <div class="cover-scales">⚖</div>
              <div class="cover-divider" />
              <div class="cover-series">The Benevolism Series — Volume II</div>
              <div class="cover-author">ENRICO MICHAEL SEVERE</div>
              <div class="cover-heir">Davidic Heir, Son of King David</div>
            </div>
            <div class="badge">
              <div>
                <div class="badge-top">BENEVOLISM SERIES</div>
                <div class="badge-main">VOLUME</div>
                <div class="badge-bottom">II</div>
              </div>
            </div>
          </div>
          <div class="info-col intro-rise-delayed">
            <div class="eyebrow" style="text-align:left;">Volume II — The Benevolism Series</div>
            <h1 class="info-title">The Benevolent Cure</h1>
            <p class="info-sub">How Benevolism Would Transform Healthcare, Restore Affordability, and Heal the People the System Has Abandoned</p>
            <p class="info-desc">
              The second volume in the Benevolism Series. Four structural layers, ten binding laws, and a five-part Healthcare Pledge for transforming a healthcare system built on extraction into one built for healing — because in healthcare, we are all, eventually, patients.
            </p>
            <div class="stats-row">
              <div v-for="(s, i) in stats" :key="i" class="stat">
                <div class="stat-num">{{ statDisplay[i] }}</div>
                <div class="stat-label">{{ s.label }}</div>
              </div>
            </div>
            <div class="cta-area">
              <!--
                AMAZON LINK PLACEHOLDER
                Once the listing is live: set amazonReady to true and amazonUrl
                to the real Amazon link in the data() below — the button updates
                automatically.
              -->
              <v-btn
                class="btn-gold"
                size="large"
                rounded="0"
                elevation="0"
                :href="amazonReady ? amazonUrl : undefined"
                :target="amazonReady ? '_blank' : undefined"
                :disabled="!amazonReady"
              >
                <v-icon start>mdi-amazon</v-icon>
                {{ amazonReady ? 'Available on Amazon' : 'Coming Soon to Amazon' }}
              </v-btn>
              <div class="coming-note">The Benevolism Series, Volume II — available now.</div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- BLUEPRINT -->
      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Blueprint</div>
          <h2 class="heading">The Benevolent Health Economy</h2>
          <p class="lede">
            A comprehensive vision of what the benevolent health economy looks like when all of its components are operating together — four structural layers, working as one system.
          </p>
          <div class="layers-grid">
            <div
              v-for="(layer, i) in layers"
              :key="i"
              class="layer-card reveal"
              :style="{ transitionDelay: (i * 80) + 'ms' }"
            >
              <div class="layer-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <h3 class="layer-name">{{ layer.name }}</h3>
              <p class="layer-desc">{{ layer.desc }}</p>
            </div>
          </div>

          <!-- INTERACTIVE PRICING MODEL DEMO -->
          <div class="pricing-demo reveal">
            <h3 class="pricing-heading">Layer One in Motion: How Primary Care Gets Priced</h3>
            <p class="pricing-sub">Universal access works because no single price has to fit everyone. Toggle a model.</p>
            <div class="pricing-tabs">
              <button class="pricing-tab" :class="{ active: pricingMode === 'subscription' }" @click="pricingMode = 'subscription'">Subscription</button>
              <button class="pricing-tab" :class="{ active: pricingMode === 'sliding' }" @click="pricingMode = 'sliding'">Sliding-Scale</button>
              <button class="pricing-tab" :class="{ active: pricingMode === 'public' }" @click="pricingMode = 'public'">Publicly Funded</button>
            </div>

            <transition name="fade-swap" mode="out-in">
              <div v-if="pricingMode === 'subscription'" key="subscription" class="pricing-stage">
                <div class="pricing-visual">
                  <div class="price-bar-track">
                    <div class="price-bar-fill" :style="{ width: subscriptionBarPct + '%' }" />
                  </div>
                  <div class="price-readout">${{ subscriptionFee }}<span>/mo</span></div>
                </div>
                <p class="pricing-caption">One flat fee. Every patient pays the same amount, every month, regardless of income.</p>
              </div>

              <div v-else-if="pricingMode === 'sliding'" key="sliding" class="pricing-stage">
                <div class="pricing-visual">
                  <div class="price-bar-track">
                    <div class="price-bar-fill sliding" :style="{ width: slidingBarPct + '%' }" />
                  </div>
                  <div class="price-readout">${{ slidingCost }}<span>/mo</span></div>
                </div>
                <label class="slider-label">
                  Monthly Household Income: <strong>${{ slidingIncome.toLocaleString() }}</strong>
                  <input type="range" min="1500" max="12000" step="250" v-model.number="slidingIncome" class="income-slider" />
                </label>
                <p class="pricing-caption">Drag the slider. The price moves with the patient's ability to pay — never the other way around.</p>
              </div>

              <div v-else key="public" class="pricing-stage">
                <div class="pricing-visual pool-visual">
                  <div
                    v-for="(dot, n) in poolDots"
                    :key="n"
                    class="pool-dot"
                    :style="{ animationDelay: dot.delay + 's', '--dx': dot.dx + 'px', '--dy': dot.dy + 'px' }"
                  />
                  <div class="pool-center"><div class="pool-icon">⚕</div></div>
                </div>
                <p class="pricing-caption">Funded together through shared contribution — available to everyone, billed to no one at the point of care.</p>
              </div>
            </transition>
          </div>

          <div class="transition-path">
            <h3 class="transition-heading">The Transition Path</h3>
            <p class="stage-text" style="text-align:center; margin-bottom:32px;">
              Building the benevolent health economy from the existing extraction-based one is not a single event. It is a managed process of reform that changes incentives, prices, and institutions over time while maintaining the continuity of care that patients depend on.
            </p>
            <div class="timeline-line reveal-line" />
            <div class="transition-stages">
              <div
                v-for="(stage, i) in transitionStages"
                :key="i"
                class="reveal"
                :style="{ transitionDelay: (i * 120) + 'ms' }"
              >
                <div class="stage-label">{{ stage.label }}</div>
                <p class="stage-text">{{ stage.text }}</p>
              </div>
            </div>
            <p class="transition-close">"This is not a decade's work. It is a generation's work. But it begins with the first step, and every step is worth taking."</p>
          </div>
        </v-container>
      </section>

      <!-- TEN LAWS -->
      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Doctrine</div>
          <h2 class="heading">The Ten Laws of Benevolent Healthcare</h2>
          <p class="lede">
            The principles of Benevolism, applied to healthcare, yield binding standards — the line between a healthcare system that is genuinely benevolent and one that merely claims to be.
          </p>
          <div class="laws-grid">
            <div
              v-for="(law, i) in laws"
              :key="i"
              class="law-card reveal"
              :style="{ transitionDelay: (i * 40) + 'ms' }"
            >
              <div class="law-numeral">{{ law.numeral }}</div>
              <div>
                <div class="law-name">{{ law.name }}</div>
                <p class="law-desc">{{ law.desc }}</p>
              </div>
            </div>
          </div>
          <div class="pull-quote">
            <div class="mark">"</div>
            <blockquote>Healthcare is not a privilege earned by those who can pay for it. It is a right owed to those who are alive.</blockquote>
          </div>
        </v-container>
      </section>

      <!-- HEALTHCARE PLEDGE -->
      <section class="alt">
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">The Pledge</div>
          <h2 class="heading">The Healthcare Pledge</h2>
          <p class="lede">
            The specific expression of the Benevolent Pledge, applied to the domain of health — offered in five versions: for healthcare providers, for healthcare institutions, for policymakers, for patients, and for society.
          </p>
          <div class="pledge-tabs">
            <button
              v-for="(pledge, i) in pledges"
              :key="i"
              class="pledge-tab"
              :class="{ active: activePledge === i }"
              @click="activePledge = i"
            >
              {{ pledge.role }}
            </button>
          </div>
          <div class="pledge-panel">
            <transition name="fade-swap" mode="out-in">
              <p :key="activePledge">{{ pledges[activePledge].text }}</p>
            </transition>
          </div>
        </v-container>
      </section>

      <!-- WHAT EACH OF US CAN DO -->
      <section>
        <v-container style="max-width:1180px;">
          <div class="eyebrow" style="text-align:center;">A Call to Action</div>
          <h2 class="heading">What Each of Us Can Do</h2>
          <p class="lede">
            The transformation of healthcare does not require everyone to become a policymaker or a healthcare executive. It requires each of us to contribute what we can, from where we stand.
          </p>
          <div class="actions-grid">
            <div
              v-for="(action, i) in actions"
              :key="i"
              class="action-card reveal"
              :style="{ transitionDelay: (i * 80) + 'ms' }"
            >
              <div class="action-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <div>
                <div class="action-title">{{ action.title }}</div>
                <p class="action-text">{{ action.text }}</p>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- CLOSING -->
      <section class="closing">
        <v-container class="closing-inner reveal" style="max-width:780px;">
          <div class="mark">"</div>
          <blockquote>The greatest act of benevolence a society can perform is to build a healthcare system that heals everyone. Not the profitable ones. Not the insured ones. Everyone.</blockquote>
          <div class="attr">— Enrico Michael Severe</div>
          <div class="closing-buttons">
            <v-btn class="btn-gold" size="large" rounded="0" elevation="0" @click="scrollToTop">
              Back to the Top
            </v-btn>
            <v-btn class="btn-ghost" size="large" rounded="0" elevation="0" @click="$emit('back')">
              ← Back to Benevolism
            </v-btn>
          </div>
        </v-container>
      </section>
    </main>

    <footer>
      <div class="footer-text">
        © {{ new Date().getFullYear() }} Enrico Michael Severe. Part of
        <a href="#" @click.prevent="$emit('back')">The Benevolism Series</a>. All rights reserved.
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'BenevolentCure',
  emits: ['back'],
  data: () => ({
    activePledge: 0,
    amazonReady: true,
    amazonUrl: 'https://www.amazon.com/dp/B0H5YML4DJ/ref=sr_1_5?dib=eyJ2IjoiMSJ9.RAhaHUXT97zeaeHcYzHnjCXe1nWSGCDMVaBhfVR_V7Xa8fTZ-Q8Xc2ICg_riCD_WCwx-S04VHrhmcpettJ-291JpdBfpPbvObYOJJsbCuaebALg9s_FT2gJMkoDpARYNsesEbJNbYL5sefKsBIVXTzFEi1qclI8w1I2rx3aHbQgyEehm2JKurjqqiyW8Q2Ee.v_2ounSwhDwLiUIPwWAYWFnAGupsQY_CFNnk0SJilmA&dib_tag=se&qid=1781827395&refinements=p_27%3AEnrico+Michael+Severe&s=books&sr=1-5&text=Enrico+Michael+Severe',
    pricingMode: 'subscription',
    subscriptionFee: 60,
    slidingIncome: 4000,
    poolDots: [
      { dx: -90, dy: -40, delay: 0 },
      { dx: 90, dy: -40, delay: 0.5 },
      { dx: -90, dy: 40, delay: 1.0 },
      { dx: 90, dy: 40, delay: 1.5 },
      { dx: 0, dy: -65, delay: 2.0 },
      { dx: 0, dy: 65, delay: 2.5 },
    ],
    stats: [
      { label: 'Structural Layers', value: 4 },
      { label: 'Laws', value: 10 },
      { label: 'Pledges', value: 5 },
      { label: 'Series Volume', value: 'II' },
    ],
    statDisplay: [0, 0, 0, 'II'],
    layers: [
      {
        name: 'Universal Primary Care Access',
        desc: 'Every person, regardless of income, geography, or insurance status, has access to a primary care provider through a subscription, sliding-scale, or publicly funded model. Primary care is the foundation on which everything else is built, and it is available to all.',
      },
      {
        name: 'Transparent Specialist & Hospital Care',
        desc: 'A competitive market governed by anti-extraction pricing standards, universal advance price disclosure, and prohibition of balance billing. Patients can access specialist or hospital care at prices they understand in advance, with no one facing financial catastrophe from a medical event.',
      },
      {
        name: 'Affordable Pharmaceutical Access',
        desc: 'Cost-based transparent pricing for all medications, generic drug market reforms, and subscription models for chronic disease medication management. No patient rations their medications for financial reasons.',
      },
      {
        name: 'Universal Risk-Pooling',
        desc: 'A system that covers all residents for necessary care, funded through income-related contributions and public subsidy for those who cannot afford them — large enough for genuine protection, simple enough to administer, governed by patient-aligned oversight.',
      },
    ],
    transitionStages: [
      {
        label: 'Begins With',
        text: 'Price transparency requirements that make costs visible. Administrative simplification that reduces overhead without changing clinical models. Expansion of community health center funding. Support for direct primary care practices already operating on benevolent models.',
      },
      {
        label: 'Proceeds Through',
        text: 'Pharmaceutical price negotiation authority. Insurance market regulation enforcing community rating. Hospital payment reforms that shift reimbursement from volume to value. Investment in rural and underserved healthcare infrastructure.',
      },
      {
        label: 'Culminates In',
        text: 'The full architecture of the benevolent health economy: universal primary care access, transparent and affordable specialist and hospital care, cost-based pharmaceutical pricing, and a universal risk pool.',
      },
    ],
    laws: [
      { numeral: 'I', name: 'The Law of Universal Access', desc: 'Healthcare is a human necessity, not a consumer choice. A system that prices any necessary service beyond the reach of any person who needs it has failed its primary obligation. Universal access is the minimum standard.' },
      { numeral: 'II', name: 'The Law of Cost-Based Pricing', desc: 'Every healthcare price must be grounded in a transparent accounting of genuine costs plus a fair return. The captive demand of patients who cannot defer treatment must never be exploited.' },
      { numeral: 'III', name: 'The Law of Radical Price Transparency', desc: 'Every patient must know the cost of their care before they commit to receiving it. No surprise bills, no balance billing, no opaque fee structures revealed only after care is delivered.' },
      { numeral: 'IV', name: 'The Law of Administrative Minimalism', desc: 'Every dollar of administrative cost must be genuinely necessary for the coordination of care. Infrastructure that exists to support extraction must be eliminated.' },
      { numeral: 'V', name: 'The Law of Medication Affordability', desc: 'No patient should ever choose between taking prescribed medications and meeting other basic needs. A drug patients cannot afford to take is not a medical solution — it is a medical failure.' },
      { numeral: 'VI', name: 'The Law of Mental Health Parity', desc: 'Mental health and physical health are equal components of human wellbeing. Mental health access must be universal, affordable, and integrated.' },
      { numeral: 'VII', name: 'The Law of Geographic Justice', desc: 'Healthcare quality and availability must not vary by geography in ways that reflect market failure rather than clinical necessity. Healthcare deserts are a policy choice, not an inevitability.' },
      { numeral: 'VIII', name: 'The Law of Patient Data Sovereignty', desc: 'Patients own their health data. Institutions that collect and use it do so as stewards, not owners. Patients must have full access, the ability to transfer records freely, and a share of any commercial value generated.' },
      { numeral: 'IX', name: 'The Law of Outcome-Aligned Incentives', desc: 'Providers, institutions, and systems must be paid in proportion to the health outcomes they produce, not the volume of services delivered.' },
      { numeral: 'X', name: 'The Law of Intergenerational Health Investment', desc: 'The health of children and young people is the most important investment a society can make in its future — nutrition, mental health, chronic disease prevention, dental care, and developmental support.' },
    ],
    pledges: [
      { role: 'Provider', text: "I became a healthcare provider to heal people. I commit to practicing in a way that honors that purpose. I will treat every patient as a person in need of care, not a source of revenue. I will recommend only the care that my best clinical judgment indicates is necessary, without regard to its billing value. I will be transparent with my patients about their options, their costs, and my reasoning. I will advocate for the patients I serve within the institutions I work for, and I will speak up when institutional incentives drive care away from their interests. I will support the transformation of the healthcare system toward structures that align my interests with patient health." },
      { role: 'Institution', text: 'This institution was built to serve the health of the community. We commit to conducting our operations in a way that honors that purpose. We will set our prices transparently, at levels that reflect genuine cost plus a fair return, not at levels designed to exploit the vulnerability of patients who need our services. We will invest in the health of the community we serve, recognizing that a healthy community is our most important institutional asset. We will eliminate administrative complexity that serves extraction rather than care. We will govern ourselves in a way that places patient health at the center of every material decision we make.' },
      { role: 'Policymaker', text: 'I was entrusted with authority over healthcare policy to advance the health of the people I represent. I commit to exercising that authority in the interest of patients, not in the interest of the industries I regulate. I will require price transparency, fight predatory billing practices, and invest in healthcare infrastructure for underserved communities. I will resist the regulatory capture that has allowed extraction to dominate a system built for healing. I will measure the success of healthcare policy by how many people can access care, how many preventable conditions are prevented, and how many families are protected from medical bankruptcy — not by the financial performance of healthcare corporations.' },
      { role: 'Patient', text: "I commit to engaging with the healthcare system on behalf of my own health and the health of the community I inhabit. I will seek preventive care, manage chronic conditions consistently, and take an active role in my own health decisions. I will choose healthcare providers and institutions that operate on benevolent terms when I have the choice to do so. I will advocate for fair healthcare pricing and accessible healthcare services in my community. And I will support the transformation of the healthcare system toward structures that serve patients rather than extract from them — because in healthcare, we are all, eventually, patients." },
      { role: 'Society', text: "We commit to building a healthcare system worthy of the people it serves. We will design our institutions, our policies, and our incentive structures to prioritize human health over financial extraction. We will invest in the healthcare infrastructure — primary care, mental health, prevention, rural services — that the extraction model has abandoned. We will hold accountable those who exploit the vulnerability of the sick. We will treat healthcare access as a right that we owe each other, not a privilege that some have earned and others must struggle to afford. And we will measure our civilization's progress, in part, by how well we care for the most vulnerable among us when they need care most." },
    ],
    actions: [
      { title: 'Patients', text: 'Advocate for transparent pricing from providers, choose subscription and direct primary care models when available, support organizations working for healthcare access, and vote for representatives who prioritize patient interests over industry interests.' },
      { title: 'Healthcare Providers', text: 'Adopt benevolent pricing models, advocate within institutions for patient-centered governance, and use professional authority to speak publicly about the relationship between pricing and access.' },
      { title: 'Administrators & Executives', text: 'Redesign institutions around benevolent principles, measuring success by patient health and community trust rather than by margin alone.' },
      { title: 'Investors', text: 'Deploy capital in alignment with Benevolist principles, backing healthcare innovators who are building for access and fairness rather than for extraction.' },
    ],
  }),
  computed: {
    subscriptionBarPct () {
      return Math.round((this.subscriptionFee / 250) * 100)
    },
    slidingCost () {
      const raw = this.slidingIncome * 0.025
      return Math.max(20, Math.min(250, Math.round(raw / 5) * 5))
    },
    slidingBarPct () {
      return Math.round((this.slidingCost / 250) * 100)
    },
  },
  mounted () {
    window.scrollTo({ top: 0 })
    this.animateStats()
    this.$nextTick(() => this.setupReveal())
  },
  beforeUnmount () {
    if (this._revealObserver) this._revealObserver.disconnect()
  },
  methods: {
    scrollToTop () {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    animateStats () {
      const duration = 900
      this.stats.forEach((s, i) => {
        if (typeof s.value !== 'number') {
          this.$set(this.statDisplay, i, s.value)
          return
        }
        const target = s.value
        const start = performance.now()
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration)
          this.$set(this.statDisplay, i, Math.round(target * progress))
          if (progress < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      })
    },
    setupReveal () {
      const els = this.$el.querySelectorAll('.reveal, .reveal-line')
      if (!('IntersectionObserver' in window)) {
        els.forEach((el) => el.classList.add('is-visible'))
        return
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            observer.unobserve(entry.target)
          }
        })
      }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' })
      els.forEach((el) => observer.observe(el))
      this._revealObserver = observer
    },
  },
}
</script>

<style>
.cure-page {
  --emerald: #0E2A1C;
  --emerald-mid: #153827;
  --emerald-light: #1E4A33;
  background: var(--emerald);
  color: var(--ivory);
  font-family: var(--font-body);
}

/* ===== ENTRANCE + SCROLL REVEAL ===== */
@keyframes introRise {
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: translateY(0); }
}
.cure-page .intro-rise { animation: introRise 0.8s cubic-bezier(.16,1,.3,1) both; }
.cure-page .intro-rise-delayed { animation: introRise 0.8s cubic-bezier(.16,1,.3,1) 0.12s both; }

.cure-page .reveal {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.cure-page .reveal.is-visible { opacity: 1; transform: translateY(0); }

.cure-page .reveal-line {
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 0.9s ease;
}
.cure-page .reveal-line.is-visible { transform: scaleX(1); }

.cure-page .top-bar {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(14,42,28,0.92);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(201,168,76,0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 32px;
}
.cure-page .back-link {
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--gold-dim);
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}
.cure-page .back-link:hover { color: var(--gold); }
.cure-page .top-brand {
  font-family: var(--font-ui);
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.18em;
  color: var(--ivory-dim);
  display: flex;
  align-items: center;
  gap: 8px;
}
.cure-page .top-brand .mark {
  width: 24px; height: 24px;
  background: var(--gold);
  color: var(--emerald);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 13px;
}

.cure-page .eyebrow {
  font-family: var(--font-ui);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: var(--gold);
  text-transform: uppercase;
  margin-bottom: 16px;
}
.cure-page .heading {
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 700;
  color: var(--ivory);
  line-height: 1.18;
  margin-bottom: 20px;
  text-align: center;
}
.cure-page .lede {
  font-family: var(--font-body);
  font-size: 17px;
  line-height: 1.75;
  color: var(--ivory-dim);
  font-weight: 300;
  text-align: center;
  max-width: 720px;
  margin: 0 auto 56px;
}
.cure-page section { padding: 96px 0; }
.cure-page section.alt { background: var(--emerald-mid); border-top: 1px solid rgba(201,168,76,0.1); border-bottom: 1px solid rgba(201,168,76,0.1); }

.cure-page .hero { padding: 72px 0 96px; position: relative; overflow: hidden; }
.cure-page .hero::before {
  content: '';
  position: absolute;
  top: -200px; right: -200px;
  width: 700px; height: 700px;
  background: radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 70%);
  pointer-events: none;
}
.cure-page .hero-inner {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 80px;
  align-items: start;
  position: relative;
  z-index: 1;
}
@media (max-width: 900px) {
  .cure-page .hero-inner { grid-template-columns: 1fr; }
  .cure-page .cover-col { display: flex; flex-direction: column; align-items: center; }
}
.cure-page .cover-col { position: relative; }
.cure-page .cover-card {
  width: 260px;
  min-height: 360px;
  background: linear-gradient(160deg, #123524 0%, #0E2A1C 60%, #061309 100%);
  border: 1px solid rgba(201,168,76,0.4);
  box-shadow: 0 0 0 1px rgba(201,168,76,0.1), 8px 8px 32px rgba(0,0,0,0.6), inset 0 0 60px rgba(201,168,76,0.03);
  padding: 32px 22px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 9px;
  transform: rotate(-2deg);
  transition: transform 0.3s;
  position: relative;
  overflow: hidden;
}
.cure-page .cover-card::before {
  content: '';
  position: absolute;
  inset: 6px;
  border: 1px solid rgba(201,168,76,0.2);
  pointer-events: none;
}
.cure-page .cover-card::after {
  content: '';
  position: absolute;
  top: 0; left: -150%;
  width: 60%; height: 100%;
  background: linear-gradient(120deg, transparent, rgba(232,204,128,0.18), transparent);
  transform: skewX(-20deg);
  transition: left 0.7s ease;
  pointer-events: none;
}
.cure-page .cover-card:hover::after { left: 150%; }
.cure-page .cover-card:hover { transform: rotate(0deg) scale(1.02); }
.cure-page .cover-crown { font-size: 34px; margin-bottom: 2px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.cure-page .cover-title {
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 400;
  color: var(--gold-light);
  letter-spacing: 0.08em;
  line-height: 1.4;
}
.cure-page .cover-title strong { font-size: 21px; font-weight: 900; display: block; letter-spacing: 0.02em; }
.cure-page .cover-divider {
  width: 80%; height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  margin: 4px 0;
}
.cure-page .cover-sub { font-family: var(--font-body); font-size: 11px; color: var(--ivory-dim); line-height: 1.5; font-style: italic; }
.cure-page .cover-scales { font-size: 30px; filter: sepia(1) hue-rotate(15deg) saturate(3) brightness(1.1); margin: 4px 0; }
.cure-page .cover-series { font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.15em; color: var(--ivory-dim); }
.cure-page .cover-author { font-family: var(--font-display); font-size: 12px; font-weight: 700; color: var(--gold-light); letter-spacing: 0.12em; }
.cure-page .cover-heir { font-family: var(--font-body); font-size: 9px; font-style: italic; color: var(--ivory-dim); }

.cure-page .badge {
  position: absolute;
  top: -12px; right: -12px;
  width: 76px; height: 76px;
  background: var(--gold);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--emerald);
  animation: badgePop 0.6s cubic-bezier(.34,1.56,.64,1) 0.5s both;
}
@keyframes badgePop {
  from { opacity: 0; transform: rotate(15deg) scale(0.4); }
  to { opacity: 1; transform: rotate(15deg) scale(1); }
}
.cure-page .badge-top { font-family: var(--font-ui); font-size: 7px; font-weight: 700; letter-spacing: 0.08em; }
.cure-page .badge-main { font-family: var(--font-display); font-size: 11px; font-weight: 900; line-height: 1.1; }
.cure-page .badge-bottom { font-family: var(--font-ui); font-size: 8px; font-weight: 600; }

.cure-page .info-title {
  font-family: var(--font-display);
  font-size: clamp(30px, 3.4vw, 46px);
  font-weight: 700;
  color: var(--ivory);
  margin-bottom: 8px;
  line-height: 1.15;
}
.cure-page .info-sub {
  font-family: var(--font-display);
  font-size: 17px;
  font-style: italic;
  color: var(--gold-dim);
  margin-bottom: 22px;
  line-height: 1.5;
}
.cure-page .info-desc {
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.75;
  color: var(--ivory-dim);
  margin-bottom: 32px;
  font-weight: 300;
  max-width: 620px;
}
.cure-page .stats-row {
  display: flex;
  align-items: center;
  gap: 0;
  margin-bottom: 36px;
  border: 1px solid rgba(201,168,76,0.15);
  width: fit-content;
}
.cure-page .stat { padding: 16px 26px; text-align: center; position: relative; }
.cure-page .stat:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 1px;
  height: 60px;
  background: rgba(201,168,76,0.15);
}
.cure-page .stat-num { font-family: var(--font-display); font-size: 30px; font-weight: 700; color: var(--gold); line-height: 1; }
.cure-page .stat-label { font-family: var(--font-ui); font-size: 9px; letter-spacing: 0.13em; color: var(--slate-light); text-transform: uppercase; margin-top: 4px; }

.cure-page .cta-area { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
.cure-page .btn-gold { background: var(--gold) !important; color: var(--emerald) !important; font-family: var(--font-ui) !important; font-size: 13px !important; font-weight: 700 !important; letter-spacing: 0.08em !important; padding: 0 32px !important; height: 52px !important; }
.cure-page .btn-gold:hover { background: var(--gold-light) !important; }
.cure-page .coming-note { font-family: var(--font-ui); font-size: 11px; color: var(--slate-light); letter-spacing: 0.02em; }

.cure-page .layers-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 2px; margin-bottom: 56px; }
.cure-page .layer-card { background: var(--emerald); border: 1px solid rgba(201,168,76,0.08); padding: 32px 28px; transition: border-color 0.2s, background 0.2s; }
.cure-page .layer-card:hover { border-color: rgba(201,168,76,0.25); background: var(--emerald-light); }
.cure-page .layer-num { font-family: var(--font-ui); font-size: 11px; font-weight: 700; color: var(--gold-dim); letter-spacing: 0.1em; margin-bottom: 12px; }
.cure-page .layer-name { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ivory); margin-bottom: 10px; }
.cure-page .layer-desc { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.cure-page .transition-path { max-width: 920px; margin: 0 auto; text-align: center; }
.cure-page .transition-heading { font-family: var(--font-display); font-size: 20px; font-weight: 600; color: var(--ivory); margin-bottom: 32px; }
.cure-page .timeline-line {
  height: 2px;
  max-width: 760px;
  margin: 0 auto 32px;
  background: linear-gradient(90deg, var(--gold-dim), var(--gold), var(--gold-dim));
}
.cure-page .transition-stages { display: grid; grid-template-columns: repeat(3, 1fr); gap: 32px; text-align: left; margin-bottom: 32px; }
@media (max-width: 768px) { .cure-page .transition-stages { grid-template-columns: 1fr; } }
.cure-page .stage-label { font-family: var(--font-ui); font-size: 11px; font-weight: 700; letter-spacing: 0.1em; color: var(--gold); text-transform: uppercase; margin-bottom: 10px; }
.cure-page .stage-text { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }
.cure-page .transition-close { font-family: var(--font-display); font-style: italic; font-size: 15px; color: var(--ivory-dim); text-align: center; }

/* ===== INTERACTIVE PRICING DEMO ===== */
.cure-page .pricing-demo { max-width: 720px; margin: 0 auto 64px; text-align: center; }
.cure-page .pricing-heading { font-family: var(--font-display); font-size: 20px; font-weight: 600; color: var(--ivory); margin-bottom: 8px; }
.cure-page .pricing-sub { font-family: var(--font-body); font-size: 14px; color: var(--ivory-dim); font-weight: 300; margin-bottom: 28px; }
.cure-page .pricing-tabs { display: flex; gap: 8px; justify-content: center; margin-bottom: 32px; flex-wrap: wrap; }
.cure-page .pricing-tab {
  background: transparent;
  border: 1px solid rgba(201,168,76,0.2);
  color: var(--ivory-dim);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  padding: 10px 20px;
  cursor: pointer;
  transition: all 0.2s;
}
.cure-page .pricing-tab:hover { border-color: var(--gold); color: var(--gold); }
.cure-page .pricing-tab.active { background: var(--gold); border-color: var(--gold); color: var(--emerald); }

.cure-page .pricing-stage { min-height: 200px; }
.cure-page .pricing-visual { display: flex; flex-direction: column; align-items: center; gap: 16px; margin-bottom: 20px; }
.cure-page .price-bar-track {
  width: 100%;
  max-width: 360px;
  height: 14px;
  background: rgba(201,168,76,0.1);
  border: 1px solid rgba(201,168,76,0.2);
  overflow: hidden;
}
.cure-page .price-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--gold-dim), var(--gold));
  transition: width 0.35s ease;
}
.cure-page .price-readout { font-family: var(--font-display); font-size: 30px; font-weight: 700; color: var(--gold); }
.cure-page .price-readout span { font-family: var(--font-ui); font-size: 13px; font-weight: 500; color: var(--ivory-dim); margin-left: 4px; }

.cure-page .slider-label {
  display: block;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--ivory-dim);
  letter-spacing: 0.04em;
  margin-bottom: 16px;
}
.cure-page .slider-label strong { color: var(--gold); font-weight: 700; }
.cure-page .income-slider {
  display: block;
  width: 100%;
  max-width: 360px;
  margin: 10px auto 0;
  appearance: none;
  -webkit-appearance: none;
  height: 4px;
  background: rgba(201,168,76,0.2);
  border-radius: 2px;
  outline: none;
}
.cure-page .income-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px; height: 18px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--emerald);
  cursor: pointer;
  transition: transform 0.15s ease;
}
.cure-page .income-slider::-webkit-slider-thumb:hover { transform: scale(1.15); }
.cure-page .income-slider::-moz-range-thumb {
  width: 18px; height: 18px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid var(--emerald);
  cursor: pointer;
}
.cure-page .pricing-caption {
  font-family: var(--font-body);
  font-size: 13px;
  line-height: 1.6;
  color: var(--ivory-dim);
  font-weight: 300;
  max-width: 460px;
  margin: 0 auto;
}

.cure-page .pool-visual { position: relative; width: 200px; height: 140px; margin: 0 auto; }
.cure-page .pool-center {
  position: absolute;
  left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  width: 56px; height: 56px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--gold) 0%, var(--gold-dim) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 24px rgba(201,168,76,0.4);
}
.cure-page .pool-icon { font-size: 22px; color: var(--emerald); }
.cure-page .pool-dot {
  position: absolute;
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--gold-light);
  top: 50%; left: 50%;
  margin: -4px;
  opacity: 0;
  animation: flowIn 3s ease-in infinite;
}
@keyframes flowIn {
  0% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(1); }
  15% { opacity: 1; }
  85% { opacity: 1; transform: translate(calc(var(--dx) * 0.08), calc(var(--dy) * 0.08)) scale(0.5); }
  100% { opacity: 0; transform: translate(0, 0) scale(0.3); }
}

/* fade-swap transition, used for pledge text and pricing stage swaps */
/* Vue 3 naming */
.cure-page .fade-swap-enter-active,
.cure-page .fade-swap-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.cure-page .fade-swap-enter-from { opacity: 0; transform: translateY(6px); }
.cure-page .fade-swap-leave-to { opacity: 0; transform: translateY(-6px); }
/* Vue 2 naming (same active/to classes, "from" state is unsuffixed) */
.cure-page .fade-swap-enter { opacity: 0; transform: translateY(6px); }
.cure-page .fade-swap-leave { opacity: 0; transform: translateY(-6px); }

.cure-page .laws-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 2px; margin-bottom: 48px; }
@media (max-width: 768px) { .cure-page .laws-grid { grid-template-columns: 1fr; } }
.cure-page .law-card { display: flex; gap: 22px; padding: 32px 36px; background: var(--emerald); border: 1px solid rgba(201,168,76,0.06); transition: border-color 0.2s, background 0.2s; }
.cure-page .law-card:hover { border-color: rgba(201,168,76,0.2); background: var(--emerald-light); }
.cure-page .law-numeral { font-family: var(--font-display); font-size: 13px; font-style: italic; color: var(--gold-dim); flex-shrink: 0; padding-top: 2px; width: 22px; }
.cure-page .law-name { font-family: var(--font-display); font-size: 16px; font-weight: 600; color: var(--ivory); margin-bottom: 9px; }
.cure-page .law-desc { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.cure-page .pull-quote { max-width: 640px; margin: 0 auto; text-align: center; }
.cure-page .pull-quote .mark { font-family: var(--font-display); font-size: 48px; color: var(--gold); line-height: 1; }
.cure-page .pull-quote blockquote { font-family: var(--font-display); font-style: italic; font-size: 19px; line-height: 1.55; color: var(--ivory); margin: 4px 0 0; }

.cure-page .pledge-tabs { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-bottom: 40px; }
.cure-page .pledge-tab { background: transparent; border: 1px solid rgba(201,168,76,0.2); color: var(--ivory-dim); font-family: var(--font-ui); font-size: 12px; font-weight: 600; letter-spacing: 0.05em; padding: 11px 22px; cursor: pointer; transition: all 0.2s; }
.cure-page .pledge-tab:hover { border-color: var(--gold); color: var(--gold); }
.cure-page .pledge-tab.active { background: var(--gold); border-color: var(--gold); color: var(--emerald); }
.cure-page .pledge-panel { max-width: 760px; margin: 0 auto; background: var(--emerald-mid); border: 1px solid rgba(201,168,76,0.12); padding: 44px 48px; }
.cure-page .pledge-panel p { font-family: var(--font-body); font-size: 15px; line-height: 1.8; color: var(--ivory-dim); font-weight: 300; }

.cure-page .actions-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 2px; }
.cure-page .action-card { display: flex; gap: 20px; padding: 34px; background: var(--emerald-mid); border: 1px solid rgba(201,168,76,0.08); transition: border-color 0.2s, background 0.2s; }
.cure-page .action-card:hover { border-color: rgba(201,168,76,0.25); background: var(--emerald-light); }
.cure-page .action-num { font-family: var(--font-ui); font-size: 11px; font-weight: 700; color: var(--gold-dim); letter-spacing: 0.1em; padding-top: 4px; width: 26px; flex-shrink: 0; }
.cure-page .action-title { font-family: var(--font-display); font-size: 17px; font-weight: 600; color: var(--ivory); margin-bottom: 10px; }
.cure-page .action-text { font-family: var(--font-body); font-size: 13px; line-height: 1.65; color: var(--ivory-dim); font-weight: 300; }

.cure-page .closing { text-align: center; padding: 110px 0 96px; position: relative; overflow: hidden; }
.cure-page .closing::before {
  content: '';
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(201,168,76,0.06) 0%, transparent 70%);
  pointer-events: none;
}
.cure-page .closing-inner { position: relative; z-index: 1; }
.cure-page .closing .mark { font-family: var(--font-display); font-size: 56px; color: var(--gold); line-height: 1; }
.cure-page .closing blockquote { font-family: var(--font-display); font-style: italic; font-size: clamp(22px, 3vw, 30px); line-height: 1.5; color: var(--ivory); margin: 8px 0 20px; }
.cure-page .closing .attr { font-family: var(--font-ui); font-size: 12px; letter-spacing: 0.15em; color: var(--gold-dim); text-transform: uppercase; margin-bottom: 48px; }
.cure-page .closing-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }
.cure-page .btn-ghost { background: transparent !important; border: 1px solid rgba(245,240,232,0.25) !important; color: var(--ivory-dim) !important; font-family: var(--font-ui) !important; font-size: 13px !important; font-weight: 500 !important; letter-spacing: 0.06em !important; padding: 0 32px !important; height: 52px !important; }
.cure-page .btn-ghost:hover { border-color: var(--ivory-dim) !important; color: var(--ivory) !important; }

.cure-page footer { padding: 40px 0; background: var(--emerald-mid); border-top: 1px solid rgba(201,168,76,0.12); text-align: center; }
.cure-page .footer-text { font-family: var(--font-ui); font-size: 11px; color: rgba(200,192,176,0.5); letter-spacing: 0.05em; }
.cure-page .footer-text a { color: var(--gold-dim); text-decoration: none; cursor: pointer; }
.cure-page .footer-text a:hover { color: var(--gold); }

/* ===== REDUCED MOTION ===== */
@media (prefers-reduced-motion: reduce) {
  .cure-page * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
  .cure-page .reveal,
  .cure-page .reveal-line {
    opacity: 1 !important;
    transform: none !important;
  }
}
</style>